import React, { useState } from 'react';
import { UserRole } from '../types/property.types';

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  active: boolean;
  createdAt: Date;
  lastLogin?: Date;
}

interface UserFormProps {
  user?: User | null;
  onSave: (userData: Partial<User>) => Promise<boolean>;
  onCancel: () => void;
  isLoading?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<Partial<User>>({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || UserRole.ASESOR,
    active: user?.active ?? true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) newErrors.name = 'Nombre es requerido';
    if (!formData.email?.trim()) newErrors.email = 'Email es requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email no válido';
    if (!formData.role) newErrors.role = 'Rol es requerido';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const success = await onSave(formData);
    if (success) {
      onCancel(); // Cerrar el formulario después de guardar
    }
  };

  const handleInputChange = (field: keyof User, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getRoleInfo = (role: UserRole) => {
    const roleConfig = {
      [UserRole.ADMIN]: { 
        label: 'Administrador', 
        description: 'Acceso completo - Puede crear/editar/eliminar propiedades y usuarios',
        color: 'text-blue-600'
      },
      [UserRole.COMERCIAL]: { 
        label: 'Comercial', 
        description: 'Solo propiedades comerciales (Local, Oficina, Bodega, Consultorio) + Arriendos',
        color: 'text-orange-600'
      },
      [UserRole.RESIDENCIAL]: { 
        label: 'Residencial', 
        description: 'Solo propiedades residenciales (Apartamentos, Casas) + Arriendos',
        color: 'text-green-600'
      },
      [UserRole.VENTAS]: { 
        label: 'Ventas', 
        description: 'Todas las propiedades + Solo ventas',
        color: 'text-purple-600'
      },
      [UserRole.CALL_CENTER]: { 
        label: 'Call Center', 
        description: 'Todas las propiedades - Sin datos de propietarios',
        color: 'text-yellow-600'
      },
      [UserRole.ASESOR]: { 
        label: 'Asesor', 
        description: 'Todas las propiedades - Sin datos de propietarios',
        color: 'text-gray-600'
      }
    };

    return roleConfig[role] || { label: role, description: '', color: 'text-gray-600' };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {user ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información Básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre Completo *
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Juan Pérez"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="juan.perez@empresa.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Rol del Usuario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rol del Usuario *
            </label>
            <div className="space-y-3">
              {Object.values(UserRole).map((role) => {
                const roleInfo = getRoleInfo(role);
                return (
                  <div key={role} className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id={role}
                      name="role"
                      value={role}
                      checked={formData.role === role}
                      onChange={(e) => handleInputChange('role', e.target.value as UserRole)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <label htmlFor={role} className={`block text-sm font-medium cursor-pointer ${roleInfo.color}`}>
                        {roleInfo.label}
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {roleInfo.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role}</p>}
          </div>

          {/* Estado del Usuario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado del Usuario
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="active"
                checked={formData.active || false}
                onChange={(e) => handleInputChange('active', e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="active" className="text-sm text-gray-700">
                Usuario activo (puede iniciar sesión)
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Los usuarios inactivos no podrán acceder al sistema
            </p>
          </div>

          {/* Información adicional para edición */}
          {user && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Información del Usuario</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Creado:</span> {user.createdAt.toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">Último acceso:</span> {
                    user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Nunca'
                  }
                </div>
                <div>
                  <span className="font-medium">ID:</span> {user.id}
                </div>
                <div>
                  <span className="font-medium">Estado:</span> 
                  <span className={`ml-1 ${user.active ? 'text-green-600' : 'text-red-600'}`}>
                    {user.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {user ? 'Actualizar' : 'Crear'} Usuario
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;